/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W3 — ENERGY WHEEL CMS-PATH RESOLVER (§33.3, §31).
 *
 * ★ REUSE SEAM (uvcCms.ts §32): the universal CMS-doc accessor
 * `getFromCmsData(doc, dot-path, id)` — a verbatim port of legacy
 * `fn.tools.get_from_cms_data` — already lives in uvcCms.ts and W1.5 flagged it
 * as the W3 reuse seam. This module IMPORTS it and adds the er_wheel path-map +
 * the er_wheel label rules. Nothing is duplicated.
 *
 * The energy_wheel selector resolves only FOUR selects through the er_wheel CMS
 * doc (energy_wheel.selector.js:44 PARAMETER_CMS_PATHS_MAPPING):
 *   mfg_id          → er_wheel.manufacturer   (label: name)
 *   series_id       → er_wheel.series         (label: series)
 *   mounting_arr_id → er_wheel.mounting_arr   (label: value || name)
 *   mtr_voltage_id  → er_wheel.mtr_voltage    (label: name || value)
 * Every OTHER dependent select (type/rotor/casing/purge/coating/…) comes from
 * the MASTER list branch (parameter_names) — those are NOT doc-resolved; they
 * render from the dependency-graph option lists.
 *
 * The doc URL is the er_wheel cms model urlRoot
 * (cms/1/v1.0.0/ahu/energy_wheels/params/), fetched once via the shared
 * getComponentCmsDoc RTKQ query (URL-keyed, 24h cache).
 */

import { getFromCmsData, type UvcCmsDoc } from './uvcCms';
import { PARAMETER_CMS_PATHS_MAPPING } from './energyWheelSelectorData';
import type { CmsNamedRow } from '../cmsDoc';
import type { OptionRow } from '../formKit';

export type EnergyWheelCmsDoc = UvcCmsDoc;

/**
 * The option <text> for an er_wheel CMS row, field-aware (mirrors the legacy
 * renderSelects branch: mfg→name, series→series, mounting/voltage→value||name).
 */
export function ewCmsOptionLabel(row: CmsNamedRow | undefined, field?: string): string {
  if (!row) return '';
  if (field === 'mfg_id') return String(row.name ?? '');
  if (field === 'series_id' && row.series != null && row.series !== '')
    return String(row.series);
  // mounting_arr / mtr_voltage and the defensive fall-through.
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.series != null && row.series !== '') return String(row.series);
  return '';
}

/** Is this field resolved via the er_wheel CMS doc (vs a master list)? */
export function isEwCmsResolvedField(field: string): boolean {
  return field in PARAMETER_CMS_PATHS_MAPPING;
}

/**
 * The OPTION LIST for a doc-resolved er_wheel select. ids == null ⇒ FULL list
 * (read-only seeds the full list, a superset, so the saved value renders); W3's
 * dependency graph passes the narrowed id-array for series/etc. value=row.id;
 * label=ewCmsOptionLabel(row). The raw row is spread so callers read
 * series/value/generic_mfg.
 */
export function ewCmsOptionList(
  doc: EnergyWheelCmsDoc | undefined,
  field: string,
  ids?: Array<number | string> | null
): OptionRow[] {
  const path = PARAMETER_CMS_PATHS_MAPPING[field];
  if (!path) return [];
  const result = getFromCmsData(doc, path, ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: ewCmsOptionLabel(row, field),
  })) as OptionRow[];
}

/** DISPLAY LABEL for the currently-selected id of a doc-resolved er_wheel select. */
export function ewCmsSelectedLabel(
  doc: EnergyWheelCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined
): string {
  if (id == null || id === '') return '';
  const path = PARAMETER_CMS_PATHS_MAPPING[field];
  if (!path) return '';
  const row = getFromCmsData(doc, path, id);
  return ewCmsOptionLabel(Array.isArray(row) ? row[0] : row, field);
}
